import { useState } from "react";
import { useEffect } from "react";
import Papa from "papaparse";
import { INTAKE_FORMS } from "../pages/intakeDefinitions";
import { ClientSnapshot } from "./ClientSnapshot";

// CLIENT HUB — internal staff tool for Andrea + Robert. One shared password,
// verified server-side; the session is a 30-day httpOnly cookie. An
// unauthenticated visitor to /client-hub gets the login screen and NOTHING
// else: every operational detail (enrollment URLs, intake backups, emergency
// Stripe links, message templates) arrives only from /api/hub/config behind
// the session. Hub API calls use same-origin relative /api paths so the
// cookie is first-party on iPhone Safari.
//
// Stateless by design: no client names, numbers, emails, or message history
// are ever stored here. The hub's ONLY job: pick program + stage, then hand
// a prepared message carrying the correct /pay/<slug> enrollment link to the
// device's own Messages/Mail app — the staff member chooses the recipient
// and presses Send themselves. Stripe stays the source of truth for
// everything after that.
//
// Stages are three DISTINCT paths — newborn / infant / toddler — each with
// its own post-payment intake form. Never combine them.

const isIOS = () => /iP(hone|ad|od)/.test(navigator.userAgent || "");

// iOS Messages needs "sms:&body="; most other platforms expect "sms:?body=".
// No recipient is ever prefilled — choosing the parent happens in Messages.
const smsHref = (body) => `sms:${isIOS() ? "&" : "?"}body=${encodeURIComponent(body)}`;
const mailHref = (subject, body) =>
  `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

// Gmail compose in a new tab — the reliable desktop path when no native
// mail app is configured (Andrea's business mail is Gmail).
const gmailHref = (subject, body) =>
  `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

// Phones/tablets get the direct native handoff; desktops get the send panel
// straight away (protocol-handler detection there is unreliable — a browser
// dialog can steal focus and look exactly like a successful handoff).
const isMobile = () =>
  /iP(hone|ad|od)|Android|Mobile/i.test(navigator.userAgent || "") ||
  (window.matchMedia && window.matchMedia("(pointer: coarse)").matches);

// Clipboard with legacy fallback — returns nothing, always shows confirmation.
const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
    } catch {
      /* clipboard unavailable — the text is visible to copy by hand */
    }
    document.body.removeChild(ta);
  }
};

const ChoiceButton = ({ testid, selected, onClick, title, sub }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-pressed={selected}
    className={`w-full rounded-3xl border px-6 py-5 text-left transition-colors duration-200 ${
      selected
        ? "border-ink bg-ink text-ivory"
        : "border-ink/15 bg-white/50 text-ink hover:border-ink/40"
    }`}
  >
    <span className="block text-lg font-medium">{title}</span>
    {sub && (
      <span className={`block mt-0.5 text-sm ${selected ? "text-ivory/70" : "text-smoke"}`}>
        {sub}
      </span>
    )}
  </button>
);

// ---------------------------------------------------------------------------
// Client record — the complete picture for one family, inside the existing
// hub card. Sections collapse so the card stays calm; the print view renders
// the same content as a clean document for the browser's Print / Save as PDF.
// Intake answers, assessment answers, and notes are client information: they
// live ONLY behind this staff session, never in tracking events.
const MONTH_FMT = { month: "long", year: "numeric" };

const monthLabel = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d) ? d.toLocaleDateString("en-US", MONTH_FMT) : "Earlier records";
};

const fmtDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d)
    ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
};

// Scheduled Calendly appointment in Andrea's local time (America/Denver) —
// date, time, AM/PM, zone abbreviation (MDT/MST). Never invented: when
// Calendly sent no start time, say so.
const fmtDenver = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d)) return "Time unavailable";
  const tz = { timeZone: "America/Denver" };
  const date = d.toLocaleDateString("en-US", { ...tz, month: "long", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { ...tz, hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short" });
  return `${date} · ${time}`;
};

const answerText = (v) => (Array.isArray(v) ? v.join("; ") : typeof v === "string" && v.trim() ? v : "—");

// The parent's intake answers in the ORIGINAL questionnaire order and wording.
const IntakeAnswersView = ({ c, print }) => {
  const def = c.client_stage ? INTAKE_FORMS[c.client_stage] : null;
  if (!c.intake_answers) {
    return (
      <p data-testid={`answers-none-${c.session_id}`} className="text-xs leading-relaxed text-smoke">
        {c.intake_status === "complete"
          ? "This intake was completed on the original Google Form — those answers live in the Google Forms responses."
          : "No intake answers yet."}
      </p>
    );
  }
  if (!def) return <p className="text-xs text-smoke">Answers are stored, but the child stage is missing — assign it above to read them in order.</p>;
  return (
    <div data-testid={`answers-list-${c.session_id}`} className={print ? "" : "max-h-[28rem] overflow-y-auto pr-1"}>
      {def.questions.map((q, i) => (
        <div key={q.key} className={`${i === 0 ? "" : "mt-4"} ${print ? "break-inside-avoid" : ""}`}>
          <p className="text-[10px] tracking-[0.18em] uppercase font-semibold text-smoke">
            {i + 1}. {q.wording.trim()}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-ink whitespace-pre-line">
            {answerText(c.intake_answers[q.key])}
          </p>
        </div>
      ))}
    </div>
  );
};

// ---------- Historical Google Forms intake import ----------
// Andrea exports a form's responses as CSV; its columns are the ORIGINAL
// question wordings, which map 1:1 onto the native q-keys (wordings live in
// one place — intakeDefinitions). All matching/safety rules are server-side.
const normHeader = (s) => (s || "").trim().replace(/\s+/g, " ").toLowerCase();

const mapCsvToRows = (stage, parsed) => {
  const def = INTAKE_FORMS[stage];
  // Blank/duplicate Google Forms headers arrive as __blank_<i> (transformHeader)
  // so no column is ever clobbered; they stay scannable for emails and raw-kept.
  const headers = (parsed.meta.fields || []).filter(Boolean);
  const byNorm = {};
  def.questions.forEach((q) => { byNorm[normHeader(q.wording)] = q; });
  const colOf = {};
  headers.forEach((h) => {
    if (h.startsWith("__blank_")) { colOf[h] = null; return; }
    const n = normHeader(h);
    colOf[h] = n === "timestamp" ? "timestamp" : byNorm[n] ? byNorm[n].key : null;
  });
  const emailQ = def.questions.find((q) => normHeader(q.wording).includes("email"));
  // Parent's name, not the child's: prefer parent/mom wordings.
  const nameQ =
    def.questions.find((q) => /parent|mom/.test(normHeader(q.wording)) && normHeader(q.wording).includes("name")) ||
    def.questions.find((q) => normHeader(q.wording).includes("name"));
  const emailRe = /\S+@\S+\.\S+/;
  const tsHeader = headers.find((h) => colOf[h] === "timestamp");
  const rows = (parsed.data || [])
    .filter((r) => headers.some((h) => (r[h] || "").trim()))
    .map((r) => {
      const answers = {};
      headers.forEach((h) => {
        const key = colOf[h];
        if (!key || key === "timestamp") return;
        const v = (r[h] || "").trim();
        if (!v) return;
        answers[key] =
          byNorm[normHeader(h)].type === "checkboxes"
            ? v.split(";").map((x) => x.trim()).filter(Boolean)
            : v;
      });
      let email = emailQ ? String(answers[emailQ.key] || "").trim() : "";
      if (!emailRe.test(email)) {
        email = "";
        for (const h of headers) {
          const m = (r[h] || "").match(emailRe);
          if (m) { email = m[0].toLowerCase(); break; }
        }
      }
      return {
        email,
        name: nameQ ? String(answers[nameQ.key] || "").trim() : "",
        submitted_at: tsHeader ? (r[tsHeader] || "").trim() : "",
        answers,
        raw: r,
      };
    });
  return { rows, unmapped: headers.filter((h) => !colOf[h]).map((h) => (h.startsWith("__blank_") ? "(unlabeled column)" : h)) };
};

const IMPORT_ACTION_LABEL = {
  imported: "Matched",
  flagged: "Needs review",
  skipped: "Skipped",
  preserved_unmatched: "No client — preserved",
};

const IntakeImportPanel = ({ onApplied }) => {
  const [stage, setStage] = useState("newborn");
  const [parsed, setParsed] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    setReport(null);
    if (!file) { setParsed(null); return; }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h, i) => (h && h.trim() ? h : `__blank_${i}`),
      complete: (res) => {
        const { rows, unmapped } = mapCsvToRows(stage, res);
        setParsed({ fileName: file.name, rows, unmapped });
      },
    });
  };

  const run = async (dryRun) => {
    if (!parsed || !parsed.rows.length) return;
    setBusy(true);
    try {
      const resp = await fetch("/api/hub/intakes/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage, dry_run: dryRun, rows: parsed.rows }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setReport({ ...data, applied: !dryRun });
        if (!dryRun) await onApplied();
      }
    } catch {
      /* unchanged state makes the failure visible */
    }
    setBusy(false);
  };

  return (
    <div data-testid="intake-import" className="mt-4 rounded-2xl border border-ink/10 bg-ivory/70 p-4">
      <p className="text-[10px] tracking-[0.22em] uppercase font-semibold text-ink/70">
        Import Historical Google Forms Intakes
      </p>
      <p className="mt-1 text-xs leading-relaxed text-smoke">
        In Google Forms: Responses → ⋮ → Download responses (.csv). Pick the matching form, upload the
        CSV, and preview first — nothing is written until you apply. Rows match clients by email;
        anything uncertain is flagged for your review, never guessed. Existing answers are never
        overwritten and no duplicate profiles are created.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {["newborn", "infant", "toddler"].map((s) => (
          <button
            key={s}
            data-testid={`intake-import-stage-${s}`}
            onClick={() => { setStage(s); setParsed(null); setReport(null); }}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-medium capitalize transition-colors duration-200 ${
              stage === s ? "border-ink bg-ink text-ivory" : "border-ink/15 text-smoke hover:text-ink"
            }`}
          >
            {s}
          </button>
        ))}
        <input
          key={stage}
          data-testid="intake-import-file"
          type="file"
          accept=".csv,text/csv"
          onChange={onFile}
          className="text-xs text-smoke file:mr-3 file:rounded-full file:border-0 file:bg-linen file:px-3.5 file:py-1.5 file:text-xs file:font-medium file:text-ink hover:file:bg-ink/10"
        />
      </div>
      {parsed && (
        <div data-testid="intake-import-parsed" className="mt-3 text-xs leading-relaxed text-smoke">
          <p>
            {parsed.fileName}: {parsed.rows.length} submission{parsed.rows.length === 1 ? "" : "s"} read.
            {parsed.unmapped.length > 0 && (
              <span data-testid="intake-import-unmapped" className="text-clay">
                {" "}Unmapped columns (kept in the raw record, not shown as answers): {parsed.unmapped.join(", ")}
              </span>
            )}
          </p>
          <button
            data-testid="intake-import-preview"
            onClick={() => run(true)}
            disabled={busy || !parsed.rows.length}
            className="mt-3 rounded-full bg-ink text-ivory text-xs font-medium px-4 py-2 hover:bg-espresso disabled:opacity-50 transition-colors duration-200"
          >
            {busy ? "Checking…" : "Preview Import"}
          </button>
        </div>
      )}
      {report && (
        <div data-testid="intake-import-report" className="mt-3 rounded-2xl border border-ink/10 bg-linen/60 p-4 text-xs leading-relaxed text-smoke">
          <p className="font-medium text-ink">
            {report.applied ? "Import applied." : "Preview — nothing written yet."}{" "}
            {report.counts.imported} matched, {report.counts.flagged} flagged for review,{" "}
            {report.counts.skipped} skipped, {report.counts.preserved_unmatched} preserved without a client.
          </p>
          <ul data-testid="intake-import-rows" className="mt-2 space-y-1">
            {report.rows.map((r, i) => (
              <li key={`${r.email || "row"}-${i}`} data-testid={`intake-import-row-${i}`}>
                <span className={r.action === "flagged" ? "font-medium text-clay" : ""}>
                  [{IMPORT_ACTION_LABEL[r.action] || r.action}]
                </span>{" "}
                {r.email || "no email"} — {r.reason}
              </li>
            ))}
          </ul>
          {!report.applied && report.counts.imported + report.counts.preserved_unmatched > 0 && (
            <button
              data-testid="intake-import-apply"
              onClick={() => run(false)}
              disabled={busy}
              className="mt-3 rounded-full bg-ink text-ivory text-xs font-medium px-4 py-2 hover:bg-espresso disabled:opacity-50 transition-colors duration-200"
            >
              {busy ? "Applying…" : "Apply This Import"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};


const RecordSection = ({ title, testid, defaultOpen, children }) => (
  <details data-testid={testid} open={defaultOpen || undefined} className="rounded-2xl border border-ink/10 bg-ivory/70">
    <summary className="cursor-pointer select-none px-4 py-3 text-[10px] tracking-[0.22em] uppercase font-semibold text-ink/70">
      {title}
    </summary>
    <div className="px-4 pb-4 pt-1">{children}</div>
  </details>
);

const RecordRow = ({ label, children }) => (
  <div className="flex gap-3 py-1 text-sm">
    <span className="w-32 shrink-0 text-[10px] tracking-[0.15em] uppercase font-semibold text-smoke pt-0.5">{label}</span>
    <span className="text-ink leading-relaxed">{children || "—"}</span>
  </div>
);

const REVIEW_LABELS = { not_scheduled: "Not scheduled", scheduled: "Scheduled", complete: "Complete" };

// One assessment card row — shared by the Open/Completed month folders.
const AssessmentRow = ({ a, openAssessment, setOpenAssessment, assessmentBusy, onToggleStatus }) => (
  <div
    data-testid={`assessment-${a.id}`}
    className="rounded-3xl border border-ink/10 bg-linen/60 p-4"
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="font-medium text-ink text-sm">
          {a.parent_name || a.email || "Assessment"}
        </p>
        <p className="mt-0.5 text-xs text-smoke">
          {[a.email, a.child_age, fmtDenver(a.event_start)]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <span
        data-testid={`assessment-status-${a.id}`}
        className={`shrink-0 rounded-full px-3 py-1 text-[10px] tracking-[0.15em] uppercase font-semibold ${
          a.status === "Assessment Completed" ? "bg-euca/15 text-euca" : "bg-ink/5 text-ink/70"
        }`}
      >
        {a.status}
      </span>
    </div>
    {a.enrolled && (
      <p data-testid={`assessment-enrolled-${a.id}`} className="mt-2 text-xs font-medium text-euca">
        Enrolled — this family is now a client (see Clients below).
      </p>
    )}
    {a.needs_review && (
      <p data-testid={`assessment-flag-${a.id}`} className="mt-2 text-xs font-medium text-clay">
        Needs review: booking arrived without an email — match it by hand.
      </p>
    )}
    <div className="mt-3 flex items-center gap-4">
      <button
        data-testid={`view-assessment-${a.id}`}
        onClick={() => setOpenAssessment(openAssessment === a.id ? null : a.id)}
        className="text-xs text-smoke underline decoration-ink/15 underline-offset-4 hover:text-ink transition-colors duration-200"
      >
        {openAssessment === a.id ? "Hide Assessment" : "View Assessment"}
      </button>
      {!a.enrolled && (
        <button
          data-testid={`assessment-toggle-${a.id}`}
          onClick={() => onToggleStatus(a.id, a.status === "Assessment Completed" ? "scheduled" : "completed")}
          disabled={assessmentBusy === a.id}
          className="text-xs text-smoke underline decoration-ink/15 underline-offset-4 hover:text-ink disabled:opacity-50 transition-colors duration-200"
        >
          {assessmentBusy === a.id
            ? "Saving…"
            : a.status === "Assessment Completed"
              ? "Reopen"
              : "Mark Complete"}
        </button>
      )}
    </div>
    {openAssessment === a.id && (
      <div data-testid={`assessment-detail-${a.id}`} className="mt-3 border-t border-ink/10 pt-3">
        <AssessmentView
          a={{ ...a.assessment, event_start: a.event_start }}
          testid={`assessment-qa-${a.id}`}
          showEmptyAppointment
        />
      </div>
    )}
  </div>
);

// Month folders — by the SCHEDULED appointment date. Newest month first,
// chronological inside each month; current + future months open, past closed.
const assessmentMonths = (list) => {
  const groups = {};
  for (const a of list) {
    const iso = a.event_start || a.booked_at || "";
    const key = /^\d{4}-\d{2}/.test(String(iso)) ? String(iso).slice(0, 7) : "0000";
    (groups[key] = groups[key] || { key, label: key === "0000" ? "No date" : monthLabel(iso), list: [] }).list.push(a);
  }
  return Object.values(groups)
    .sort((x, y) => y.key.localeCompare(x.key))
    .map((g) => ({ ...g, list: [...g.list].sort((x, y) => String(x.event_start || "").localeCompare(String(y.event_start || ""))) }));
};

// Shared read-only assessment rendering — website questionnaire answers plus,
// when present, the Calendly appointment time (Denver local) and booking Q&A.
// Used by BOTH the Assessments detail and the client card: same data, one layout.
const AssessmentView = ({ a, testid, showEmptyAppointment }) => (
  <div>
    {(a.event_start || showEmptyAppointment) && (
      <RecordRow label="Appointment">{fmtDenver(a.event_start)}</RecordRow>
    )}
    <RecordRow label="Submitted">{fmtDate(a.submitted_at)}</RecordRow>
    <RecordRow label="Parent">{a.parent_name}</RecordRow>
    <RecordRow label="Child's age">{a.child_age}</RecordRow>
    <RecordRow label="Hardest">{(a.hardest || []).join("; ")}</RecordRow>
    <RecordRow label="Happening">{(a.context || []).join("; ")}</RecordRow>
    <RecordRow label="Feelings">{(a.emotional || []).join("; ")}</RecordRow>
    <RecordRow label="Where they are">{a.intent}</RecordRow>
    <RecordRow label="Tried">{a.tried}</RecordRow>
    <RecordRow label="Contact">{[a.preferred_contact, a.phone].filter(Boolean).join(" · ")}</RecordRow>
    {(a.calendly_qa || []).length > 0 && (
      <div data-testid={testid} className="mt-3 border-t border-ink/10 pt-3">
        <p className="text-[10px] tracking-[0.22em] uppercase font-semibold text-euca">
          Booking questions
        </p>
        {a.calendly_qa.map((qa, i) => (
          <div key={i} className="mt-2.5">
            <p className="text-[10px] tracking-[0.12em] uppercase font-semibold text-smoke">
              {qa.question}
            </p>
            <p className="mt-0.5 text-sm leading-relaxed text-ink whitespace-pre-line">
              {qa.answer}
            </p>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ClientRecordSections = ({ c, notesValue, onNotesChange, onSaveNotes, notesBusy }) => (
  <div data-testid={`record-${c.session_id}`} className="mt-4 space-y-2.5">
    {c.intake_answers && INTAKE_FORMS[c.client_stage] && (
      <RecordSection title="Client Snapshot" testid={`record-snapshot-${c.session_id}`} defaultOpen>
        <ClientSnapshot stage={c.client_stage} answers={c.intake_answers} testid={`snapshot-${c.session_id}`} />
      </RecordSection>
    )}
    <RecordSection title="Client Overview" testid={`record-overview-${c.session_id}`} defaultOpen>
      <RecordRow label="Parent">{c.parent_name}</RecordRow>
      <RecordRow label="Email">{c.parent_email}</RecordRow>
      <RecordRow label="Service">{c.program}</RecordRow>
      <RecordRow label="Child stage">{c.stage_label}</RecordRow>
      <RecordRow label="Paid">{[fmtDate(c.paid_at), c.amount ? `$${c.amount.toLocaleString()}` : null].filter(Boolean).join(" · ")}</RecordRow>
      <RecordRow label="Status">{c.status.replace(/_/g, " ")}</RecordRow>
    </RecordSection>
    <RecordSection title="Initial Sleep Assessment" testid={`record-assessment-${c.session_id}`}>
      {c.assessment ? (
        <AssessmentView a={c.assessment} testid={`assessment-qa-${c.session_id}`} />
      ) : (
        <p data-testid={`assessment-none-${c.session_id}`} className="text-xs leading-relaxed text-smoke">
          No website questionnaire found for this client&rsquo;s email. Answers given inside the
          Calendly booking itself live in Calendly, not here.
        </p>
      )}
    </RecordSection>
    <RecordSection title="Intake" testid={`record-intake-${c.session_id}`}>
      <RecordRow label="Status">{c.intake_status === "complete" ? `Complete${c.intake_completed_at ? ` · ${fmtDate(c.intake_completed_at)}` : ""}` : "Pending"}</RecordRow>
      {c.intake_source === "google_forms_import" && (
        <RecordRow label="Source">Historical Google Form — imported; original submission date kept</RecordRow>
      )}
      <div className="mt-2">
        <IntakeAnswersView c={c} />
      </div>
    </RecordSection>
    <RecordSection title="Sleep Plan Review" testid={`record-review-${c.session_id}`}>
      <RecordRow label="Status">{REVIEW_LABELS[c.plan_review_status] || c.plan_review_status}</RecordRow>
      {c.plan_review_marked_at && <RecordRow label="Marked">{fmtDate(c.plan_review_marked_at)}</RecordRow>}
      {/* Pre-call prep: the IDENTICAL ClientSnapshot (same component, same
          stored answers, same per-stage mapping) so Andrea can open just
          this section before the call. Nothing new is stored or mapped. */}
      {c.intake_answers && INTAKE_FORMS[c.client_stage] && (
        <div className="mt-3 border-t border-ink/10 pt-3">
          <p className="text-[10px] tracking-[0.22em] uppercase font-semibold text-euca">
            Pre-call snapshot
          </p>
          <div className="mt-2">
            <ClientSnapshot stage={c.client_stage} answers={c.intake_answers} testid={`review-snapshot-${c.session_id}`} />
          </div>
        </div>
      )}
    </RecordSection>
    <RecordSection title="Andrea's Notes" testid={`record-notes-${c.session_id}`}>
      <textarea
        data-testid={`notes-input-${c.session_id}`}
        value={notesValue}
        onChange={(e) => onNotesChange(e.target.value)}
        rows={4}
        placeholder="Private notes — only you see these."
        className="w-full rounded-xl border border-ink/15 bg-ivory px-3.5 py-3 text-sm leading-relaxed text-ink placeholder:text-smoke/50 focus:border-clay focus:outline-none"
      />
      <div className="mt-2 flex items-center gap-4">
        <button
          data-testid={`notes-save-${c.session_id}`}
          onClick={onSaveNotes}
          disabled={notesBusy}
          className="rounded-full bg-ink text-ivory text-xs font-medium px-4 py-2 hover:bg-espresso disabled:opacity-50 transition-colors duration-200"
        >
          {notesBusy ? "Saving…" : "Save Notes"}
        </button>
        {c.internal_notes_updated_at && (
          <span data-testid={`notes-updated-${c.session_id}`} className="text-[11px] text-smoke">
            Last updated {fmtDate(c.internal_notes_updated_at)}
          </span>
        )}
      </div>
    </RecordSection>
  </div>
);

// Print Client Record — the same record as a clean document. Rendered in
// place of the hub; the print stylesheet strips the buttons.
const ClientRecordPrint = ({ c, onBack }) => (
  <div className="min-h-svh bg-white text-ink">
    <div className="no-print max-w-3xl mx-auto px-6 pt-8 flex gap-5">
      <button data-testid="print-now" onClick={() => window.print()} className="rounded-full bg-ink text-ivory text-sm font-medium px-6 py-3 hover:bg-espresso transition-colors duration-200">
        Print / Save as PDF
      </button>
      <button data-testid="print-back" onClick={onBack} className="text-sm text-smoke underline underline-offset-4 decoration-ink/20 hover:text-ink">
        Back to Client Hub
      </button>
    </div>
    <div data-testid="print-record" className="max-w-3xl mx-auto px-6 py-10">
      <p className="font-serif font-light text-2xl tracking-tight">The Nurtured Parent Co.</p>
      <p className="mt-0.5 text-[9px] tracking-[0.3em] uppercase text-smoke">Client Record — Confidential</p>
      <h1 className="mt-8 font-serif font-light text-3xl tracking-tight">{c.parent_name || c.parent_email || "Client"}</h1>
      <div className="mt-6 border-t border-ink/15 pt-4">
        <RecordRow label="Parent">{c.parent_name}</RecordRow>
        <RecordRow label="Email">{c.parent_email}</RecordRow>
        <RecordRow label="Service">{c.program}</RecordRow>
        <RecordRow label="Child stage">{c.stage_label}</RecordRow>
        <RecordRow label="Paid">{[fmtDate(c.paid_at), c.amount ? `$${c.amount.toLocaleString()}` : null].filter(Boolean).join(" · ")}</RecordRow>
        <RecordRow label="Status">{c.status.replace(/_/g, " ")}</RecordRow>
      </div>
      {c.intake_answers && INTAKE_FORMS[c.client_stage] && (
        <div className="mt-8 border-t border-ink/15 pt-4 break-inside-avoid">
          <h2 className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">Client Snapshot</h2>
          <div className="mt-2">
            <ClientSnapshot stage={c.client_stage} answers={c.intake_answers} testid={`print-snapshot-${c.session_id}`} />
          </div>
        </div>
      )}
      <div className="mt-8 border-t border-ink/15 pt-4 break-inside-avoid">
        <h2 className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">Initial Sleep Assessment</h2>
        <div className="mt-2">{c.assessment ? <AssessmentView a={c.assessment} /> : <p className="text-sm text-smoke">No website questionnaire on record for this email.</p>}</div>
      </div>
      <div className="mt-8 border-t border-ink/15 pt-4">
        <h2 className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">
          Intake{c.intake_completed_at ? ` — completed ${fmtDate(c.intake_completed_at)}` : ""}
        </h2>
        <div className="mt-3"><IntakeAnswersView c={c} print /></div>
      </div>
      <div className="mt-8 border-t border-ink/15 pt-4 break-inside-avoid">
        <h2 className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">Sleep Plan Review</h2>
        <div className="mt-2">
          <RecordRow label="Status">{REVIEW_LABELS[c.plan_review_status] || c.plan_review_status}</RecordRow>
          {c.plan_review_marked_at && <RecordRow label="Marked">{fmtDate(c.plan_review_marked_at)}</RecordRow>}
        </div>
      </div>
      <div className="mt-8 border-t border-ink/15 pt-4 break-inside-avoid">
        <h2 className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">Andrea&rsquo;s Notes</h2>
        <p className="mt-2 text-sm leading-relaxed whitespace-pre-line">{c.internal_notes || "—"}</p>
      </div>
    </div>
  </div>
);


export const ClientHub = () => {
  const [auth, setAuth] = useState(null); // null = checking | false = login | true = hub
  const [config, setConfig] = useState(null);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [busy, setBusy] = useState(false);
  const [program, setProgram] = useState(null);
  const [stage, setStage] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedMsg, setCopiedMsg] = useState(false);
  // "text" | "email" | null — inline fallback when the device can't hand the
  // sms:/mailto: link to a native app (desktop with no SMS handler, iPhone
  // with no Mail account configured, embedded preview panes, …).
  const [sendFallback, setSendFallback] = useState(null);
  // Client pipeline — every paid purchase as a card (paid → intake → review).
  const [clients, setClients] = useState(null);
  const [clientBusy, setClientBusy] = useState("");
  // Assessments — booked complimentary assessments not yet converted to paid.
  const [assessments, setAssessments] = useState(null);
  const [openAssessment, setOpenAssessment] = useState(null);
  // Legacy cleanup — inline editor for records that predate the automatic
  // service/stage/intake fields. Pre-filled with current values so saving
  // can never blank out valid existing data.
  const [editingClient, setEditingClient] = useState(null);
  const [editForm, setEditForm] = useState({});

  const openEditor = (c) => {
    setEditingClient(c.session_id);
    setEditForm({
      service_type: c.service_type || "",
      client_stage: c.client_stage || "",
      intake_status: c.intake_status || "pending",
      plan_review_status: c.plan_review_status || "not_scheduled",
    });
  };

  const saveEditor = async (sessionId) => {
    const payload = Object.fromEntries(Object.entries(editForm).filter(([, v]) => v));
    await clientPost("edit", sessionId, payload);
    setEditingClient(null);
  };

  // Client record — search, month grouping, expanded profile, notes, print.
  const [searchTerm, setSearchTerm] = useState("");
  const [openRecord, setOpenRecord] = useState(null);
  const [notesDrafts, setNotesDrafts] = useState({});
  const [notesBusy, setNotesBusy] = useState(false);
  const [printClient, setPrintClient] = useState(null);

  // Legacy auto-backfill — evidence-only restore of service/stage on records
  // that predate those fields. Dry-run first, then explicit apply.
  const [backfill, setBackfill] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);

  const runBackfill = async (dryRun) => {
    setBackfillBusy(true);
    try {
      const resp = await fetch("/api/hub/clients/backfill", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dry_run: dryRun }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setBackfill(dryRun ? { ...data, applied: false } : { ...data, applied: true });
        if (!dryRun) await loadClients();
      }
    } catch {
      /* unchanged state makes the failure visible */
    }
    setBackfillBusy(false);
  };

  const saveNotes = async (sessionId) => {
    setNotesBusy(true);
    try {
      const resp = await fetch("/api/hub/clients/notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, notes: notesDrafts[sessionId] ?? "" }),
      });
      if (resp.ok) await loadClients();
    } catch {
      /* unchanged state makes the failure visible */
    }
    setNotesBusy(false);
  };

  // Manual open/complete for assessments — Andrea's call, same record.
  const [assessmentBusy, setAssessmentBusy] = useState("");
  const setAssessmentStatus = async (id, status) => {
    setAssessmentBusy(id);
    try {
      const resp = await fetch("/api/hub/assessments/status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, status }),
      });
      if (resp.ok) await loadClients();
    } catch {
      /* unchanged state makes the failure visible */
    }
    setAssessmentBusy("");
  };

  // Name/email search → filtered, then grouped by payment month (newest
  // first — the server already sorts desc). Current month opens by default;
  // while searching every group stays open so matches can't hide.
  const visibleClients = clients
    ? clients.clients.filter((c) => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return true;
        return [c.parent_name, c.parent_email].filter(Boolean).join(" ").toLowerCase().includes(q);
      })
    : [];
  const clientGroups = [];
  for (const c of visibleClients) {
    const label = monthLabel(c.paid_at);
    const last = clientGroups[clientGroups.length - 1];
    if (last && last.label === label) last.list.push(c);
    else clientGroups.push({ label, list: [c] });
  }
  const currentMonth = monthLabel(new Date().toISOString());

  const loadClients = async () => {
    try {
      const resp = await fetch("/api/hub/clients");
      if (resp.ok) setClients(await resp.json());
    } catch {
      /* the clients view is additive — the rest of the hub still works */
    }
    try {
      const resp = await fetch("/api/hub/assessments");
      if (resp.ok) setAssessments(await resp.json());
    } catch {
      /* same — additive */
    }
  };

  // Quick actions on a client card: resend links / mark plan-review status.
  const clientPost = async (kind, sessionId, extra = {}) => {
    setClientBusy(`${kind}:${sessionId}`);
    try {
      const resp = await fetch(`/api/hub/clients/${kind}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, ...extra }),
      });
      if (!resp.ok) throw new Error("action failed");
      await loadClients();
    } catch {
      /* state stays unchanged, so the failure is visible */
    }
    setClientBusy("");
  };

  // Internal page: never indexed, and branded cleanly for iPhone
  // Share → Add to Home Screen ("Client Hub" + Andrea's portrait icon).
  useEffect(() => {
    document.title = "Client Hub";
    const added = [];
    const meta = (name, content) => {
      const m = document.createElement("meta");
      m.name = name;
      m.content = content;
      document.head.appendChild(m);
      added.push(m);
    };
    meta("robots", "noindex,nofollow");
    meta("apple-mobile-web-app-capable", "yes");
    meta("apple-mobile-web-app-title", "Client Hub");
    meta("apple-mobile-web-app-status-bar-style", "default");
    const icon = document.createElement("link");
    icon.rel = "apple-touch-icon";
    icon.href = "/client-hub-icon.png";
    document.head.appendChild(icon);
    added.push(icon);
    window.scrollTo(0, 0);
    return () => {
      added.forEach((el) => document.head.removeChild(el));
      document.title = "The Nurtured Parent Co. — Pediatric Sleep Consulting";
    };
  }, []);

  // Existing session? Then load the hub's operational config with it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch("/api/hub/session");
        if (!resp.ok) throw new Error("not authenticated");
        const cfg = await fetch("/api/hub/config");
        if (!cfg.ok) throw new Error("config failed");
        const data = await cfg.json();
        if (!cancelled) {
          setConfig(data);
          setAuth(true);
          loadClients();
        }
      } catch {
        if (!cancelled) setAuth(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    if (busy || !password) return;
    setBusy(true);
    setLoginError("");
    try {
      const resp = await fetch("/api/hub/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(typeof data.detail === "string" ? data.detail : "Sign-in failed");
      }
      const cfg = await fetch("/api/hub/config");
      if (!cfg.ok) throw new Error("Could not load Client Hub");
      setConfig(await cfg.json());
      setPassword("");
      setAuth(true);
      loadClients();
    } catch (err) {
      setLoginError(err.message || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => {
    try {
      await fetch("/api/hub/logout", { method: "POST" });
    } catch {
      /* cookie expires on its own */
    }
    setAuth(false);
    setConfig(null);
    setProgram(null);
    setStage(null);
  };

  const onCopy = async (url) => {
    await copyText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // Attempt the native handoff (Messages/Mail). If ~1s passes with no app
  // switch (page never blurs/hides), the device has no handler — reveal the
  // prepared message inline so it can be copied into any app instead.
  const attemptNative = (kind, href) => (e) => {
    e.preventDefault();
    setSendFallback(null);
    let handedOff = false;
    const onLeave = () => {
      handedOff = true;
    };
    window.addEventListener("blur", onLeave, { once: true });
    document.addEventListener("visibilitychange", onLeave, { once: true });
    window.location.href = href;
    window.setTimeout(() => {
      window.removeEventListener("blur", onLeave);
      document.removeEventListener("visibilitychange", onLeave);
      if (!handedOff) setSendFallback(kind);
    }, 900);
  };

  if (auth === null) {
    return (
      <main className="min-h-svh bg-ivory text-ink flex items-center justify-center px-6">
        <p data-testid="hub-loading" role="status" className="text-xs uppercase tracking-[0.25em] text-smoke">
          Client Hub
        </p>
      </main>
    );
  }

  if (auth === false) {
    return (
      <main data-testid="hub-login" className="min-h-svh bg-ivory text-ink flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <header className="text-center">
            <span className="block font-serif font-light text-2xl leading-tight tracking-tight">
              The Nurtured Parent Co.
            </span>
            <span className="block mt-1 text-[9px] tracking-[0.3em] uppercase text-smoke">
              Pediatric Sleep Consulting
            </span>
            <h1 className="mt-12 font-serif font-light text-ink text-4xl tracking-tight">
              Client Hub
            </h1>
            <p className="mt-3 text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              Staff Login
            </p>
          </header>
          <form data-testid="hub-login-form" onSubmit={onLogin} className="mt-10 space-y-5">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-smoke">Password</span>
              <input
                data-testid="hub-password-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full rounded-2xl border border-ink/15 bg-white/60 px-5 py-4 text-lg text-ink outline-none focus:border-clay transition-colors duration-200"
              />
            </label>
            {loginError && (
              <p data-testid="hub-login-error" className="text-sm text-clay">
                {loginError}
              </p>
            )}
            <button
              data-testid="hub-sign-in"
              type="submit"
              disabled={busy || !password}
              className="w-full rounded-full bg-ink text-ivory font-medium px-8 py-4 text-lg hover:bg-espresso disabled:opacity-50 transition-colors duration-300"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  if (printClient) {
    return <ClientRecordPrint c={printClient} onBack={() => setPrintClient(null)} />;
  }

  const selectedSlug = program && stage ? `${program}-${stage}` : null;
  const selectedUrl = selectedSlug ? config.enrollment_links[selectedSlug] : null;
  const STAGE_LABELS = { newborn: "Newborn", infant: "Infant", toddler: "Toddler" };
  const stageLabel = stage ? STAGE_LABELS[stage] : null;
  const smsBody = selectedUrl ? config.messages.sms.replace("{link}", selectedUrl) : "";
  const emailBody = selectedUrl ? config.messages.email.replace("{link}", selectedUrl) : "";

  // Text/Email: phones hand straight to Messages/Mail (with a timed fallback
  // if no app answers); desktops open the send panel immediately instead of
  // relying on protocol-handler detection.
  const onSend = (kind) => (e) => {
    e.preventDefault();
    setCopiedMsg(false);
    if (isMobile()) {
      const href = kind === "text" ? smsHref(smsBody) : mailHref(config.messages.email_subject, emailBody);
      attemptNative(kind, href)(e);
    } else {
      setSendFallback(kind);
    }
  };

  const STAGE_LINK_LABELS = {
    "virtual-newborn": "Virtual — Newborn",
    "virtual-infant": "Virtual — Infant",
    "virtual-toddler": "Virtual — Toddler",
    "in-home-newborn": "In-Home — Newborn",
    "in-home-infant": "In-Home — Infant",
    "in-home-toddler": "In-Home — Toddler",
  };
  // Internal intake backups point at the SAME native intake routes the live
  // paid-client journey uses (the form depends only on stage; virtual is the
  // canonical variant). A family's own verified link — which opens the form
  // with their saved progress — is on their client card above. The legacy
  // Google Form URLs stay stored, demoted to emergency-only.
  const site = config.quick_links.website.replace(/\/$/, "");
  const INTAKE_LINKS = [
    { testid: "intake-virtual-newborn", label: "Virtual — Newborn Intake (0–3 months)", url: `${site}/virtual-newborn` },
    { testid: "intake-virtual-infant", label: "Virtual — Infant Intake (4–17 months)", url: `${site}/virtual-infant` },
    { testid: "intake-virtual-toddler", label: "Virtual — Toddler Intake (18+ months)", url: `${site}/virtual-toddler` },
    { testid: "intake-in-home-newborn", label: "In-Home — Newborn Intake (0–3 months)", url: `${site}/in-home-newborn` },
    { testid: "intake-in-home-infant", label: "In-Home — Infant Intake (4–17 months)", url: `${site}/in-home-infant` },
    { testid: "intake-in-home-toddler", label: "In-Home — Toddler Intake (18+ months)", url: `${site}/in-home-toddler` },
  ];
  const LEGACY_INTAKE_LINKS = [
    { testid: "legacy-intake-newborn", label: "Newborn — legacy Google Form", url: config.quick_links.intake_newborn },
    { testid: "legacy-intake-infant", label: "Infant — legacy Google Form", url: config.quick_links.intake_infant },
    { testid: "legacy-intake-toddler", label: "Toddler — legacy Google Form", url: config.quick_links.intake_toddler },
  ];
  const STRIPE_LINK_LABELS = { virtual: "Virtual checkout", "in-home": "In-Home checkout" };

  const quietLink =
    "text-smoke underline decoration-ink/15 underline-offset-4 hover:text-ink transition-colors duration-200 break-all";

  return (
    <main data-testid="client-hub" className="min-h-svh bg-ivory text-ink">
      <div className="max-w-xl mx-auto px-5 pt-8 pb-16">
        <header className="flex items-start justify-between gap-4">
          <div>
            <span className="block font-serif font-light text-xl leading-tight tracking-tight">
              The Nurtured Parent Co.
            </span>
            <span className="block mt-0.5 text-[9px] tracking-[0.3em] uppercase text-smoke">
              Client Hub
            </span>
          </div>
          <button
            data-testid="hub-sign-out"
            onClick={onLogout}
            className="mt-1.5 text-xs text-smoke hover:text-ink underline underline-offset-4 decoration-ink/20 transition-colors duration-200"
          >
            Sign Out
          </button>
        </header>

        {/* Assessments — booked complimentary assessments not yet converted.
            The beginning of the family's record; payment moves them to
            Clients below with their answers attached. */}
        {assessments && (
          <section className="mt-12" data-testid="hub-assessments">
            <h1 className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              Assessments
            </h1>
            <p data-testid="assessments-sync-status" className="mt-1.5 text-[11px] text-smoke/80">
              {assessments.sync?.last_error_at
                ? "Calendly sync hit an error — last booking event failed to process. Tell Andrea's developer."
                : assessments.sync?.last_event_at
                  ? `Calendly sync active — last booking received ${fmtDate(assessments.sync.last_event_at)}.`
                  : "Calendly sync — waiting for the first booking to arrive."}
            </p>
            {assessments.assessments.length === 0 ? (
              <p data-testid="hub-assessments-empty" className="mt-4 text-sm leading-relaxed text-smoke">
                No booked assessments waiting. When a family books their
                complimentary assessment, they appear here.
              </p>
            ) : (
              <div className="mt-4 space-y-6">
                {[
                  ["Open", assessments.assessments.filter((a) => a.status !== "Assessment Completed")],
                  ["Completed", assessments.assessments.filter((a) => a.status === "Assessment Completed")],
                ].map(([group, list]) => {
                  if (list.length === 0) return null;
                  const nowKey = new Date().toISOString().slice(0, 7);
                  return (
                    <div key={group} data-testid={`assessments-${group.toLowerCase()}`}>
                      <p className="text-[10px] tracking-[0.22em] uppercase font-semibold text-smoke/70">
                        {group} · {list.length}
                      </p>
                      {assessmentMonths(list).map((m) => (
                        <details
                          key={m.key}
                          data-testid={`assessments-${group.toLowerCase()}-${m.key}`}
                          open={m.key >= nowKey || undefined}
                          className="mt-2"
                        >
                          <summary className="cursor-pointer select-none flex items-baseline gap-3">
                            <span className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">{m.label}</span>
                            <span className="text-[10px] text-smoke/70">{m.list.length} assessment{m.list.length === 1 ? "" : "s"}</span>
                          </summary>
                          <div className="mt-2 space-y-3">
                            {m.list.map((a) => (
                              <AssessmentRow
                                key={a.id}
                                a={a}
                                openAssessment={openAssessment}
                                setOpenAssessment={setOpenAssessment}
                                assessmentBusy={assessmentBusy}
                                onToggleStatus={setAssessmentStatus}
                              />
                            ))}
                          </div>
                        </details>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Clients — the operational center. Who paid, where they are, who
            needs attention. Send Client Enrollment below remains as the
            manual override, not the daily workflow. */}
        {clients && (
          <section className="mt-12" data-testid="hub-clients">
            <h1 className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              Clients
            </h1>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="hub-clients-summary">
              {[
                ["active", clients.summary.active, n => `${n} Active`],
                ["intake-pending", clients.summary.intake_pending, n => `${n} Intake Pending`],
                ["intake-complete", clients.summary.intake_complete, n => `${n} Intake Complete`],
                ["reviews-scheduled", clients.summary.reviews_scheduled, n => `${n} Review${n === 1 ? "" : "s"} Scheduled`],
                ["needs-attention", clients.summary.needs_attention, n => `${n} Need${n === 1 ? "s" : ""} Attention`],
              ].map(([key, n, format]) => (
                <span
                  key={key}
                  data-testid={`summary-${key}`}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-medium ${
                    key === "needs-attention" && n > 0
                      ? "border-clay/40 text-clay"
                      : "border-ink/15 text-smoke"
                  }`}
                >
                  {format(n)}
                </span>
              ))}
            </div>

            {clients.clients.length === 0 && (
              <p data-testid="hub-clients-empty" className="mt-6 text-sm leading-relaxed text-smoke">
                No paid clients yet. When a payment completes, the family
                appears here automatically.
              </p>
            )}

            <input
              data-testid="hub-client-search"
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search clients — name or email"
              className="mt-5 w-full rounded-2xl border border-ink/15 bg-white/60 px-5 py-3.5 text-sm text-ink placeholder:text-smoke/50 focus:border-clay focus:outline-none transition-colors duration-200"
            />

            <div className="mt-4 flex items-center gap-4">
              <button
                data-testid="backfill-run"
                onClick={() => runBackfill(true)}
                disabled={backfillBusy}
                className="text-xs text-smoke underline decoration-ink/15 underline-offset-4 hover:text-ink disabled:opacity-50 transition-colors duration-200"
              >
                {backfillBusy ? "Checking…" : "Auto-Fix Legacy Records"}
              </button>
            </div>
            {backfill && (
              <div data-testid="backfill-report" className="mt-3 rounded-2xl border border-ink/10 bg-linen/60 p-4 text-xs leading-relaxed text-smoke">
                <p className="font-medium text-ink">
                  {backfill.applied ? "Backfill applied." : "Dry run — nothing written yet."}{" "}
                  {backfill.records_examined} legacy record{backfill.records_examined === 1 ? "" : "s"} examined:{" "}
                  {backfill.fully_backfilled} fully backfilled, {backfill.partially_backfilled} partially,{" "}
                  {backfill.needs_manual_review} need{backfill.needs_manual_review === 1 ? "s" : ""} manual review.
                </p>
                {backfill.manual_review.length > 0 && (
                  <ul data-testid="backfill-manual-list" className="mt-2 space-y-1">
                    {backfill.manual_review.map((m) => (
                      <li key={m.session_id}>
                        {m.email || m.session_id} — missing: {m.missing_fields.join(", ")}
                      </li>
                    ))}
                  </ul>
                )}
                {!backfill.applied && (backfill.fully_backfilled > 0 || backfill.partially_backfilled > 0) && (
                  <button
                    data-testid="backfill-apply"
                    onClick={() => runBackfill(false)}
                    disabled={backfillBusy}
                    className="mt-3 rounded-full bg-ink text-ivory text-xs font-medium px-4 py-2 hover:bg-espresso disabled:opacity-50 transition-colors duration-200"
                  >
                    {backfillBusy ? "Applying…" : "Apply These Fixes"}
                  </button>
                )}
              </div>
            )}

            <IntakeImportPanel onApplied={loadClients} />

            {visibleClients.length === 0 && clients.clients.length > 0 && (
              <p data-testid="hub-clients-no-match" className="mt-6 text-sm text-smoke">
                No clients match that search.
              </p>
            )}

            {clientGroups.map((g) => (
              <details
                key={g.label}
                data-testid={`month-group-${g.label.replace(/\s+/g, "-").toLowerCase()}`}
                open={searchTerm.trim() !== "" || g.label === currentMonth || g.label === "Earlier records" || undefined}
                className="mt-6"
              >
                <summary className="cursor-pointer select-none flex items-baseline gap-3">
                  <span className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">{g.label}</span>
                  <span className="text-[10px] text-smoke/70">{g.list.length} client{g.list.length === 1 ? "" : "s"}</span>
                </summary>
                <div className="mt-4 space-y-4">
              {g.list.map((c) => {
                const STATUS_LABELS = {
                  intake_pending: "Intake Pending",
                  intake_complete: "Intake Complete",
                  plan_review_scheduled: "Review Scheduled",
                  plan_review_complete: "Review Complete",
                };
                const paidOn = c.paid_at
                  ? new Date(c.paid_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  : null;
                const welcomeUrl = c.route
                  ? `${config.quick_links.website.replace(/\/$/, "")}${c.route}?session_id=${c.session_id}`
                  : null;
                const intakeUrl = c.client_stage
                  ? config.quick_links[`intake_${c.client_stage}`]
                  : null;
                return (
                  <article
                    key={c.session_id}
                    data-testid={`hub-client-${c.session_id}`}
                    className={`rounded-3xl border p-5 ${c.needs_attention ? "border-clay/40 bg-linen/80" : "border-ink/10 bg-linen/60"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-ink text-base">
                          {c.parent_name || c.parent_email || "Client"}
                        </p>
                        <p className="mt-0.5 text-xs text-smoke">
                          {[c.program, c.stage_label, paidOn, c.amount ? `$${c.amount.toLocaleString()}` : null]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                      <span
                        data-testid={`status-${c.session_id}`}
                        className={`shrink-0 rounded-full px-3 py-1 text-[10px] tracking-[0.15em] uppercase font-semibold ${
                          c.status === "plan_review_complete"
                            ? "bg-euca/15 text-euca"
                            : "bg-ink/5 text-ink/70"
                        }`}
                      >
                        {STATUS_LABELS[c.status] || c.status}
                      </span>
                    </div>

                    {c.needs_attention && (
                      <div data-testid={`flags-${c.session_id}`} className="mt-3 space-y-1">
                        {c.flags.map((f) => (
                          <p key={f} className="text-xs font-medium text-clay">
                            Needs attention: {f}
                          </p>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2.5 text-sm">
                      {intakeUrl && (
                        <a data-testid={`open-intake-${c.session_id}`} href={intakeUrl} target="_blank" rel="noopener noreferrer" className="text-ink underline decoration-ink/20 underline-offset-4 hover:decoration-clay transition-colors duration-200">
                          Open Intake
                        </a>
                      )}
                      {welcomeUrl && (
                        <a data-testid={`open-welcome-${c.session_id}`} href={welcomeUrl} target="_blank" rel="noopener noreferrer" className="text-ink underline decoration-ink/20 underline-offset-4 hover:decoration-clay transition-colors duration-200">
                          Open Welcome Page
                        </a>
                      )}
                      <button
                        data-testid={`resend-intake-${c.session_id}`}
                        onClick={() => clientPost("resend", c.session_id, { kind: "intake" })}
                        disabled={clientBusy === `resend:${c.session_id}`}
                        className="text-smoke underline decoration-ink/15 underline-offset-4 hover:text-ink disabled:opacity-50 transition-colors duration-200"
                      >
                        Resend Intake Link
                      </button>
                      <button
                        data-testid={`send-review-${c.session_id}`}
                        onClick={() => clientPost("resend", c.session_id, { kind: "review" })}
                        disabled={clientBusy === `resend:${c.session_id}`}
                        className="text-smoke underline decoration-ink/15 underline-offset-4 hover:text-ink disabled:opacity-50 transition-colors duration-200"
                      >
                        Send Plan Review Link
                      </button>
                      {c.status !== "plan_review_complete" && (
                        <button
                          data-testid={`advance-${c.session_id}`}
                          onClick={() =>
                            clientPost("status", c.session_id, {
                              plan_review_status:
                                c.status === "plan_review_scheduled" || c.plan_review_status === "scheduled"
                                  ? "complete"
                                  : "scheduled",
                            })
                          }
                          disabled={clientBusy === `status:${c.session_id}`}
                          className="text-smoke underline decoration-ink/15 underline-offset-4 hover:text-ink disabled:opacity-50 transition-colors duration-200"
                        >
                          {c.plan_review_status === "scheduled" ? "Mark Plan Review Complete" : "Mark Review Scheduled"}
                        </button>
                      )}
                      <button
                        data-testid={`edit-client-${c.session_id}`}
                        onClick={() => (editingClient === c.session_id ? setEditingClient(null) : openEditor(c))}
                        className="text-smoke underline decoration-ink/15 underline-offset-4 hover:text-ink transition-colors duration-200"
                      >
                        {editingClient === c.session_id ? "Close Editor" : "Edit Details"}
                      </button>
                      <button
                        data-testid={`record-toggle-${c.session_id}`}
                        onClick={() => setOpenRecord(openRecord === c.session_id ? null : c.session_id)}
                        className="text-smoke underline decoration-ink/15 underline-offset-4 hover:text-ink transition-colors duration-200"
                      >
                        {openRecord === c.session_id ? "Hide Client Record" : "Client Record"}
                      </button>
                      <button
                        data-testid={`print-${c.session_id}`}
                        onClick={() => setPrintClient(c)}
                        className="text-smoke underline decoration-ink/15 underline-offset-4 hover:text-ink transition-colors duration-200"
                      >
                        Print Client Record
                      </button>
                    </div>

                    {editingClient === c.session_id && (
                      <div data-testid={`edit-panel-${c.session_id}`} className="mt-4 rounded-2xl border border-ink/10 bg-ivory/70 p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {[
                            { key: "service_type", label: "Service Type", options: [["virtual", "Virtual"], ["in-home", "In-Home"]] },
                            { key: "client_stage", label: "Child Stage", options: [["newborn", "Newborn (0–3 months)"], ["infant", "Infant (4–11 months)"], ["toddler", "Toddler (12 months +)"]] },
                            { key: "intake_status", label: "Intake Status", options: [["pending", "Pending"], ["complete", "Complete"]] },
                            { key: "plan_review_status", label: "Plan Review Status", options: [["not_scheduled", "Not Scheduled"], ["scheduled", "Scheduled"], ["complete", "Complete"]] },
                          ].map((f) => (
                            <label key={f.key} className="block">
                              <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-smoke">{f.label}</span>
                              <select
                                data-testid={`edit-${f.key}-${c.session_id}`}
                                value={editForm[f.key] || ""}
                                onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                                className="mt-1 w-full rounded-xl border border-ink/15 bg-ivory px-3 py-2.5 text-sm text-ink"
                              >
                                {!editForm[f.key] && <option value="">—</option>}
                                {f.options.map(([v, l]) => (
                                  <option key={v} value={v}>{l}</option>
                                ))}
                              </select>
                            </label>
                          ))}
                        </div>
                        <p className="mt-3 text-xs text-smoke">
                          Enrollment route:{" "}
                          <span data-testid={`edit-route-${c.session_id}`} className="font-medium text-ink">
                            {editForm.service_type && editForm.client_stage
                              ? `${editForm.service_type}-${editForm.client_stage}`
                              : "select service + stage"}
                          </span>
                        </p>
                        <div className="mt-3 flex gap-4 text-sm">
                          <button
                            data-testid={`edit-save-${c.session_id}`}
                            onClick={() => saveEditor(c.session_id)}
                            disabled={clientBusy === `edit:${c.session_id}`}
                            className="rounded-full bg-ink text-ivory font-medium px-5 py-2 hover:bg-espresso disabled:opacity-50 transition-colors duration-200"
                          >
                            {clientBusy === `edit:${c.session_id}` ? "Saving…" : "Save"}
                          </button>
                          <button
                            data-testid={`edit-cancel-${c.session_id}`}
                            onClick={() => setEditingClient(null)}
                            className="text-smoke underline decoration-ink/15 underline-offset-4 hover:text-ink transition-colors duration-200"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {openRecord === c.session_id && (
                      <ClientRecordSections
                        c={c}
                        notesValue={notesDrafts[c.session_id] ?? c.internal_notes ?? ""}
                        onNotesChange={(v) => setNotesDrafts((d) => ({ ...d, [c.session_id]: v }))}
                        onSaveNotes={() => saveNotes(c.session_id)}
                        notesBusy={notesBusy}
                      />
                    )}
                  </article>
                );
              })}
                </div>
              </details>
            ))}
          </section>
        )}

        <h1 className="mt-14 text-xs tracking-[0.25em] uppercase font-semibold text-euca">
          Send Client Enrollment
        </h1>
        <p className="mt-2 text-xs leading-relaxed text-smoke">
          Manual override — after your assessment conversation, choose the
          program and stage and the right link goes out. Everything after
          payment is automatic.
        </p>

        {/* STEP 1 — program (Andrea's recommendation, never the client's choice) */}
        <section className="mt-8">
          <p className="text-sm font-medium text-ink/70">
            <span className="font-serif italic font-light text-clay text-lg mr-2">01</span>
            Choose the program
          </p>
          <div className="mt-3 space-y-3">
            {Object.entries(config.programs).map(([key, p]) => (
              <ChoiceButton
                key={key}
                testid={`program-${key}`}
                selected={program === key}
                onClick={() => setProgram(key)}
                title={p.label}
                sub={p.price}
              />
            ))}
          </div>
        </section>

        {/* STEP 2 — child stage: three distinct paths, never combined */}
        <section className="mt-8">
          <p className="text-sm font-medium text-ink/70">
            <span className="font-serif italic font-light text-clay text-lg mr-2">02</span>
            Choose the child&rsquo;s stage
          </p>
          <div className="mt-3 space-y-3">
            <ChoiceButton
              testid="stage-newborn"
              selected={stage === "newborn"}
              onClick={() => setStage("newborn")}
              title="Newborn"
            />
            <ChoiceButton
              testid="stage-infant"
              selected={stage === "infant"}
              onClick={() => setStage("infant")}
              title="Infant"
            />
            <ChoiceButton
              testid="stage-toddler"
              selected={stage === "toddler"}
              onClick={() => setStage("toddler")}
              title="Toddler"
            />
          </div>
        </section>

        {/* STEP 3 — final visual check, then the native app does the sending */}
        {selectedUrl && (
          <section className="mt-10">
            <div data-testid="selected-enrollment" className="rounded-3xl border border-ink/10 bg-linen/60 p-5">
              <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">
                Selected enrollment
              </p>
              <p data-testid="selected-enrollment-name" className="mt-2 font-serif font-light text-ink text-2xl tracking-tight">
                {config.programs[program].label} — {stageLabel}
              </p>
              <p data-testid="selected-enrollment-url" className="mt-1.5 text-xs text-smoke break-all">
                {selectedUrl}
              </p>
            </div>
            <div className="mt-4 space-y-3">
              <a
                data-testid="text-client-btn"
                href={smsHref(smsBody)}
                onClick={onSend("text")}
                className="w-full inline-flex justify-center items-center rounded-full bg-ink text-ivory font-medium px-8 py-5 text-lg hover:bg-espresso transition-colors duration-300"
              >
                Text Client
              </a>
              <a
                data-testid="email-client-btn"
                href={mailHref(config.messages.email_subject, emailBody)}
                onClick={onSend("email")}
                className="w-full inline-flex justify-center items-center rounded-full border border-ink/25 text-ink font-medium px-8 py-5 text-lg hover:border-ink transition-colors duration-300"
              >
                Email Client
              </a>
              <div className="text-center">
                <button
                  data-testid="copy-link-btn"
                  onClick={() => onCopy(selectedUrl)}
                  className="text-sm text-smoke underline underline-offset-4 decoration-ink/20 hover:text-ink transition-colors duration-200 py-2"
                >
                  Copy Enrollment Link
                </button>
                {copied && (
                  <p data-testid="copy-confirmation" className="mt-1 text-xs text-euca">
                    Enrollment link copied.
                  </p>
                )}
              </div>

              {/* Send panel: always on desktop; on mobile only if the
                  device couldn't open Messages/Mail — the message is never lost */}
              {sendFallback && (
                <div data-testid="send-fallback-panel" className="rounded-3xl border border-ink/10 bg-linen/60 p-5">
                  <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">
                    {sendFallback === "text" ? "Text message" : "Email"} — ready to send
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-smoke">
                    Here&rsquo;s the prepared message — send it your way below,
                    or copy it and paste it into any app you like.
                  </p>
                  {sendFallback === "email" && (
                    <p data-testid="send-fallback-subject" className="mt-4 text-sm font-medium text-ink">
                      Subject: {config.messages.email_subject}
                    </p>
                  )}
                  <p data-testid="send-fallback-message" className="mt-3 text-sm leading-relaxed text-smoke whitespace-pre-line">
                    {sendFallback === "text" ? smsBody : emailBody}
                  </p>

                  {sendFallback === "email" && (
                    <div className="mt-4 space-y-2.5">
                      <a
                        data-testid="open-gmail-btn"
                        href={gmailHref(config.messages.email_subject, emailBody)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex justify-center items-center rounded-full bg-ink text-ivory text-sm font-medium px-6 py-3.5 hover:bg-espresso transition-colors duration-300"
                      >
                        Open in Gmail
                      </a>
                      <a
                        data-testid="open-default-mail-btn"
                        href={mailHref(config.messages.email_subject, emailBody)}
                        className="w-full inline-flex justify-center items-center rounded-full border border-ink/25 text-ink text-sm font-medium px-6 py-3.5 hover:border-ink transition-colors duration-300"
                      >
                        Open Default Mail App
                      </a>
                    </div>
                  )}

                  <div className="mt-4 text-center">
                    <button
                      data-testid="copy-message-btn"
                      onClick={async () => {
                        await copyText(
                          sendFallback === "email"
                            ? `Subject: ${config.messages.email_subject}\n\n${emailBody}`
                            : smsBody
                        );
                        setCopiedMsg(true);
                        setTimeout(() => setCopiedMsg(false), 2500);
                      }}
                      className={`${sendFallback === "email" ? "text-sm text-smoke underline underline-offset-4 decoration-ink/20 hover:text-ink py-2" : "rounded-full bg-ink text-ivory text-sm font-medium px-6 py-3 hover:bg-espresso"} transition-colors duration-300`}
                    >
                      Copy Message
                    </button>
                    {copiedMsg && (
                      <p data-testid="copy-message-confirmation" className="mt-2 text-xs text-euca">
                        Message copied — paste it anywhere.
                      </p>
                    )}
                  </div>
                  {sendFallback === "text" && !isMobile() && (
                    <p className="mt-3 text-center text-xs leading-relaxed text-smoke">
                      Texting opens automatically on your phone — from a computer,
                      copying the message is the reliable path.
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Admin / Backup Tools — grouped low on the page: the Clients view
            and the enrollment selector above are the daily workflow. */}
        <section className="mt-16 border-t border-ink/10 pt-8 space-y-12">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
            Admin / Backup Tools
          </p>
          <div data-testid="internal-intake-forms">
            <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">
              Internal Intake Forms — Internal / Backup
            </p>
            <p className="mt-2 text-xs leading-relaxed text-smoke">
              These open the same native intake pages families use after
              verified payment. A family&rsquo;s own link — with their saved
              progress — is on their client card above.
            </p>
            <ul className="mt-4 space-y-3.5 text-sm">
              {INTAKE_LINKS.map((l) => (
                <li key={l.testid}>
                  <a data-testid={l.testid} href={l.url} target="_blank" rel="noopener noreferrer" className={quietLink}>
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
            <details data-testid="legacy-intake-links" className="mt-4">
              <summary className="cursor-pointer select-none text-[10px] tracking-[0.2em] uppercase font-semibold text-smoke/70">
                Legacy Google Forms — emergency only
              </summary>
              <ul className="mt-3 space-y-3 text-sm">
                {LEGACY_INTAKE_LINKS.map((l) => (
                  <li key={l.testid}>
                    <a data-testid={l.testid} href={l.url} target="_blank" rel="noopener noreferrer" className={quietLink}>
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </details>
          </div>

          <div data-testid="quick-links">
            <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">
              Client Tools
            </p>
            <ul className="mt-4 space-y-3.5 text-sm">
              <li>
                <a data-testid="quick-assessment" href={config.quick_links.assessment} target="_blank" rel="noopener noreferrer" className="text-ink underline decoration-ink/20 underline-offset-4 hover:decoration-clay transition-colors duration-200">
                  Complimentary Pediatric Sleep Assessment
                </a>
              </li>
              {config.quick_links.plan_review && (
                <li>
                  <a data-testid="quick-plan-review" href={config.quick_links.plan_review} target="_blank" rel="noopener noreferrer" className="text-ink underline decoration-ink/20 underline-offset-4 hover:decoration-clay transition-colors duration-200">
                    Sleep Plan Review — 45 Minutes
                  </a>
                </li>
              )}
              <li>
                <a data-testid="quick-website" href={config.quick_links.website} target="_blank" rel="noopener noreferrer" className="text-ink underline decoration-ink/20 underline-offset-4 hover:decoration-clay transition-colors duration-200">
                  Main Website
                </a>
              </li>
            </ul>
          </div>

          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">
              Enrollment Links — Manual Backup
            </p>
            <ul data-testid="manual-enrollment-links" className="mt-4 space-y-3.5 text-sm">
              {Object.entries(config.enrollment_links).map(([slug, url]) => (
                <li key={slug}>
                  <a data-testid={`manual-link-${slug}`} href={url} target="_blank" rel="noopener noreferrer" className={quietLink}>
                    {STAGE_LINK_LABELS[slug] || slug}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div data-testid="emergency-stripe-links">
            <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-smoke">
              Emergency Payment Backup
            </p>
            <p className="mt-2 text-xs leading-relaxed text-clay">
              Using a direct Stripe link may bypass the normal Client Hub
              enrollment/intake routing. Use only if necessary.
            </p>
            <ul className="mt-3 space-y-3.5 text-sm">
              {Object.entries(config.emergency_stripe_links).map(([key, url]) => (
                <li key={key}>
                  <a data-testid={`emergency-stripe-${key}`} href={url} target="_blank" rel="noopener noreferrer" className={quietLink}>
                    {STRIPE_LINK_LABELS[key] || key}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </main>
  );
};
